import React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { ChevronDown } from 'lucide-react';
import { getContentStatus, getContentStatusMeta, toggleContentStatus } from '../lib/deals';
import { useDeals } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import { useInvoices } from '../context/InvoicesContext';
import { toast } from 'sonner';
import ContentStatusChecklist from './ContentStatusChecklist';

/**
 * DeliverablesCell — inline content-status picker on the deals table.
 * Multi-select: tick as many stages as apply; the line preview shows the most recent one.
 */
const DeliverablesCell = ({ deal }) => {
  const { updateDeal } = useDeals();
  const { refresh: refreshInvoices } = useInvoices();
  const { user } = useAuth();
  const isManager = ['manager', 'talent_manager'].includes(user?.role);

  const current = getContentStatus(deal);
  const meta = getContentStatusMeta(current);

  const toggle = async (id) => {
    const patch = toggleContentStatus(deal, id);
    const result = await updateDeal(deal.id, patch);
    if (result?._auto_invoice_created) {
      await refreshInvoices();
      toast.success('draft invoice created — content is live', { description: 'ready to review in invoicing' });
    }
    if (result?._marked_ready_to_invoice) {
      toast.success('marked ready to invoice', { description: 'now suggested on the talent invoicing tab' });
    } else if (id === 'content_live' && patch.content_statuses.includes('content_live') && (Number(deal.value) || 0) <= 0) {
      toast('content marked live', { description: 'add a £ rate to this deal so it can be suggested for invoicing' });
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1.5 h-7 px-2 py-0 rounded-full text-[11px] font-medium min-w-[120px] max-w-[220px]"
          style={{ background: `${meta.color}22`, color: meta.color }}
          data-testid={`content-status-cell-${deal.id}`}
        >
          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: meta.color }} />
          <span className="truncate">{meta.label}</span>
          <ChevronDown className="w-3 h-3 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-1" onClick={(e) => e.stopPropagation()}>
        <ContentStatusChecklist deal={deal} isManager={isManager} onToggle={toggle} />
      </PopoverContent>
    </Popover>
  );
};

export default DeliverablesCell;
